import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from '@mui/material';
import axios from 'axios';

interface TokenInfo {
  id: string;
  name: string;
  createdAt: string;
  expiresAt?: string;
  isActive: boolean;
}

interface TokenListPageProps {
  authToken: string;
}

export const TokenListPage: React.FC<TokenListPageProps> = ({ authToken }) => {
  const [tokens, setTokens] = useState<TokenInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTokenId, setSelectedTokenId] = useState<string | null>(null);
  const [selectedTokenName, setSelectedTokenName] = useState<string>('');

  // Fetch tokens on mount
  useEffect(() => {
    fetchTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const fetchTokens = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get('/api/tokens', {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (response.data.success && Array.isArray(response.data.tokens)) {
        setTokens(response.data.tokens);
      } else {
        setError('Invalid response format');
      }
    } catch (err: any) {
      console.error('Failed to fetch tokens:', err);
      setError(`Failed to load tokens: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (tokenId: string, tokenName: string) => {
    setSelectedTokenId(tokenId);
    setSelectedTokenName(tokenName);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTokenId) return;

    try {
      setLoading(true);
      const response = await axios.delete(`/api/tokens/${selectedTokenId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (response.data.success) {
        setError(null);
        await fetchTokens(); // Refresh list
      }
    } catch (err: any) {
      console.error('Failed to delete token:', err);
      setError(`Failed to delete token: ${err.response?.data?.message || err.message}`);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedTokenId(null);
      setSelectedTokenName('');
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedTokenId(null);
    setSelectedTokenName('');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Invalid date';
    }
  };

  const isTokenExpired = (expiresAt?: string): boolean => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '1000px' }}>
      <Box sx={{ mb: 3 }}>
        <h1>🔐 My API Tokens</h1>
        <p>View and manage your API tokens. Revoke tokens you no longer need.</p>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : tokens.length === 0 ? (
        <Alert severity="info">
          No API tokens created yet. Go to the <strong>Token-adm</strong> page to create one.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Token Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Expires</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((token) => (
                <TableRow key={token.id} hover>
                  <TableCell>{token.name}</TableCell>
                  <TableCell>{formatDate(token.createdAt)}</TableCell>
                  <TableCell>{formatDate(token.expiresAt)}</TableCell>
                  <TableCell>
                    {!token.isActive ? (
                      <Chip label="Revoked" size="small" variant="outlined" color="error" />
                    ) : isTokenExpired(token.expiresAt) ? (
                      <Chip label="Expired" size="small" variant="outlined" color="warning" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(token.id, token.name)}
                      disabled={!token.isActive}
                    >
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Revoke Token?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke the token <strong>"{selectedTokenName}"</strong>? 
            This will immediately invalidate the token and any API calls using it will fail. 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Revoke Token
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
